import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, ExternalLink, TrendingUp, ArrowDownAZ, ArrowUpAZ, DollarSign, Calendar } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart, registerables } from 'chart.js';
import { useAuth } from '../contexts/AuthContext';
import { compareGamePrices, getPriceHistory, getGameDetails } from '../lib/storeService';
import { getGameBySlug } from '../lib/game/detailsService';
import { StoreProduct, PriceHistory, Game } from '../types';
import SEO from '../components/SEO';
import { useTheme } from '../contexts/ThemeContext';

// Registrar componentes necessários para o Chart.js
Chart.register(...registerables);

// Tipos para os filtros
type SortBy = 'price' | 'store' | 'last_updated';
type SortDirection = 'asc' | 'desc';

const GamePricesPage: React.FC = () => {
  const { gameId, gameSlug } = useParams<{ gameId?: string; gameSlug?: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  
  // Estado para os produtos, histórico de preços e o jogo
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Estado para os filtros e ordenação
  const [sortBy, setSortBy] = useState<SortBy>('price');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<number>(3); // meses
  
  // Buscar detalhes do jogo e preços
  useEffect(() => {
    const fetchGameAndPrices = async () => {
      setLoading(true);
      try {
        let gameDetails: Game | null = null;
        
        // Carregar detalhes do jogo (primeiro por slug, depois por ID)
        if (gameSlug) {
          try {
            gameDetails = await getGameBySlug(gameSlug);
          } catch (error) {
            console.error('Erro ao buscar jogo pelo slug:', error);
            // Se falhar por slug, tentamos por ID mesmo assim
          }
        }
        
        // Se não temos detalhes ainda e temos ID, tenta por ID
        if (!gameDetails && gameId) {
          gameDetails = await getGameDetails(gameId);
        }
        
        if (gameDetails) {
          setGame(gameDetails);
          
          // Carregar preços do jogo em todas as lojas
          const productPrices = await compareGamePrices(gameDetails.id);
          setProducts(productPrices);
          
          // Carregar histórico de preços
          await fetchPriceHistory(gameDetails.id);
        } else {
          toast.error('Jogo não encontrado');
          navigate('/stores');
          return;
        }
      } catch (error) {
        console.error('Erro ao buscar detalhes do jogo e preços:', error);
        toast.error('Falha ao carregar informações do jogo');
      } finally {
        setLoading(false);
      }
    };
    
    if (gameId || gameSlug) {
      fetchGameAndPrices();
    } else {
      toast.error('ID ou slug do jogo não especificado');
      navigate('/stores');
    }
  }, [gameId, gameSlug, navigate]);
  
  // Buscar histórico de preços
  const fetchPriceHistory = useCallback(async (id: string) => {
    setHistoryLoading(true);
    try {
      // Calcular data inicial baseada no período selecionado
      const startDate = format(
        subMonths(new Date(), historyPeriod),
        'yyyy-MM-dd'
      );
      
      // Filtro para o histórico
      const filters = {
        gameId: id,
        storeId: selectedStoreId || undefined,
        startDate
      };
      
      const history = await getPriceHistory(filters);
      setPriceHistory(history);
    } catch (error) {
      console.error('Erro ao buscar histórico de preços:', error);
      toast.error('Falha ao carregar histórico de preços');
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedStoreId, historyPeriod]);
  
  // Atualizar histórico quando os filtros mudarem
  useEffect(() => {
    if (game) {
      fetchPriceHistory(game.id);
    }
  }, [fetchPriceHistory, game, selectedStoreId, historyPeriod]);
  
  // Formatar preço para exibição
  const formatPrice = (price: number | null) => {
    if (price === null) return 'Preço não informado';
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };
  
  // Ordenar produtos
  const sortedProducts = useMemo(() => {
    if (!products.length) return [];
    
    return [...products].sort((a, b) => {
      // Não exibir produtos indisponíveis no topo
      if (a.available !== b.available) {
        return a.available ? -1 : 1;
      }
      
      if (sortBy === 'price') {
        // Tratar produtos sem preço
        if (a.price === null && b.price === null) return 0;
        if (a.price === null) return 1;
        if (b.price === null) return -1;
        
        return sortDirection === 'asc' 
          ? a.price - b.price 
          : b.price - a.price;
      }
      
      if (sortBy === 'store') {
        const storeNameA = a.store?.name || '';
        const storeNameB = b.store?.name || '';
        
        return sortDirection === 'asc'
          ? storeNameA.localeCompare(storeNameB)
          : storeNameB.localeCompare(storeNameA);
      }
      
      if (sortBy === 'last_updated') {
        const dateA = new Date(a.updated_at).getTime();
        const dateB = new Date(b.updated_at).getTime();
        
        return sortDirection === 'asc'
          ? dateA - dateB
          : dateB - dateA;
      }
      
      return 0;
    });
  }, [products, sortBy, sortDirection]);
  
  // Gerar dados para o gráfico
  const chartData = useMemo(() => {
    if (!priceHistory.length) return null;
    
    const storeData: Record<string, {dates: string[], prices: number[], name: string, color: string}> = {};
    
    // Cores para as lojas (poderia vir de uma função de geração de cores)
    const colors = [
      theme === 'dark' ? 'rgb(96, 165, 250)' : 'rgb(75, 192, 192)',   // Azul / Ciano
      theme === 'dark' ? 'rgb(244, 114, 182)' : 'rgb(255, 99, 132)',  // Rosa / Vermelho claro
      theme === 'dark' ? 'rgb(52, 211, 153)' : 'rgb(54, 162, 235)',  // Verde / Azul
      theme === 'dark' ? 'rgb(251, 191, 36)' : 'rgb(255, 159, 64)', // Âmbar / Laranja
      theme === 'dark' ? 'rgb(167, 139, 250)' : 'rgb(153, 102, 255)',// Violeta / Roxo
      theme === 'dark' ? 'rgb(250, 204, 21)' : 'rgb(255, 205, 86)'   // Amarelo / Amarelo
    ];
    
    priceHistory.forEach(entry => {
      if (!entry.store_product?.store) return;
      
      const storeId = entry.store_product.store.id;
      const storeName = entry.store_product.store.name;
      
      if (!storeData[storeId]) {
        const colorIndex = Object.keys(storeData).length % colors.length;
        storeData[storeId] = {
          dates: [],
          prices: [],
          name: storeName,
          color: colors[colorIndex]
        };
      }
      
      const date = format(new Date(entry.recorded_at), 'dd/MM/yyyy', { locale: ptBR });
      storeData[storeId].dates.push(date);
      storeData[storeId].prices.push(entry.price);
    });
    
    const datasets = Object.values(storeData).map(store => ({
      label: store.name,
      data: store.prices,
      borderColor: store.color,
      backgroundColor: `${store.color}33`,
      tension: 0.1
    }));
    
    const allDates = Array.from(
      new Set(
        Object.values(storeData).flatMap(store => store.dates)
      )
    ).sort((a, b) => {
      const dateA = new Date(a.split('/').reverse().join('-'));
      const dateB = new Date(b.split('/').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });
    
    return {
      labels: allDates,
      datasets
    };
  }, [priceHistory, theme]);
  
  // Opções para o gráfico
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme === 'dark' ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))'
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? 'hsl(var(--popover))' : '#fff',
        titleColor: theme === 'dark' ? 'hsl(var(--popover-foreground))' : '#666',
        bodyColor: theme === 'dark' ? 'hsl(var(--popover-foreground))' : '#666',
        borderColor: theme === 'dark' ? 'hsl(var(--border))' : '#ddd',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatPrice(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))'
        },
        grid: {
          color: theme === 'dark' ? 'hsl(var(--border))' : 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        beginAtZero: false,
        ticks: {
          callback: function(value: number | string) { // Alterado para aceitar string também
            if (typeof value === 'number') {
              return `R$ ${value.toFixed(2)}`;
            }
            return value; // Retornar string diretamente se for o caso
          },
          color: theme === 'dark' ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))'
        },
        grid: {
          color: theme === 'dark' ? 'hsl(var(--border))' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  }), [theme]);
  
  // Alternar a direção da ordenação
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  // Atualizar campo de ordenação
  const handleSortChange = (field: SortBy) => {
    if (sortBy === field) {
      toggleSortDirection();
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  // Renderizar o ícone de direção da ordenação
  const renderSortIcon = (field: SortBy) => {
    if (sortBy !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpAZ size={16} className="ml-1" />
      : <ArrowDownAZ size={16} className="ml-1" />;
  };
  
  if (loading) {
    return (
      <div className={`container mx-auto px-4 py-6 flex justify-center items-center min-h-screen ${theme === 'dark' ? '' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!game) {
    return (
      <div className={`container mx-auto px-4 py-6 min-h-screen ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className={`${theme === 'dark' ? 'bg-destructive/20 text-destructive-foreground' : 'bg-red-100 text-red-800'} p-4 rounded-md`}>
          <p>Jogo não encontrado</p>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <SEO 
        title={`Preços: ${game.name} - Boardverso`}
        description={`Compare preços de ${game.name} em diversas lojas parceiras`}
        image={game.image_url || undefined}
      />
      <Toaster position="top-right" />
      
      <div className={`container mx-auto px-4 py-6 max-w-7xl ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
        <div className="flex items-center mb-6">
          <Link
            to="/stores"
            className={`mr-3 p-2 rounded-full ${theme === 'dark' ? 'bg-muted hover:bg-muted/80 text-foreground' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            <ArrowLeft size={20} />
          </Link>
          
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Preços de {game.name}</h1>
        </div>
        
        {/* Seção de informações do jogo */}
        <div className={`${theme === 'dark' ? 'bg-gray-800 text-card-foreground' : 'bg-white'} rounded-lg shadow-md overflow-hidden mb-8`}>
          <div className="md:flex">
            {game.image_url && (
              <div className="md:w-1/3 p-4">
                <img 
                  src={game.image_url}
                  alt={game.name}
                  className="w-full h-auto object-contain rounded-md"
                />
              </div>
            )}
            
            <div className="p-4 md:w-2/3">
              <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{game.name}</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Ano:</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>{game.year || 'Não informado'}</p>
                </div>
                
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Jogadores:</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
                    {game.min_players && game.max_players
                      ? `${game.min_players}-${game.max_players}`
                      : 'Não informado'}
                  </p>
                </div>
                
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Tempo de jogo:</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
                    {game.play_time
                      ? `${game.play_time} min`
                      : 'Não informado'}
                  </p>
                </div>
                
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>Complexidade:</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
                    {game.average_complexity
                      ? `${parseFloat(game.average_complexity.toString()).toFixed(1)}/5`
                      : 'Não informada'}
                  </p>
                </div>
              </div>
              
              {game.description && (
                <div className="mt-4">
                  <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-800'} line-clamp-4`}>
                    {game.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Seção de preços */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <h2 className={`text-xl font-bold mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <ShoppingBag size={20} className="mr-2" />
              Melhores preços
            </h2>
            
            <div className={`${theme === 'dark' ? 'bg-gray-800 text-card-foreground' : 'bg-white'} rounded-lg shadow-md overflow-hidden`}>
              {/* Cabeçalho da tabela com opções de ordenação */}
              <div className={`grid grid-cols-5 p-3 border-b ${theme === 'dark' ? 'bg-muted/50 border-border' : 'bg-gray-100 border-gray-200'}`}>
                <div className="col-span-2 font-medium">
                  <button 
                    onClick={() => handleSortChange('store')}
                    className={`flex items-center focus:outline-none ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-700 hover:text-black'}`}
                  >
                    Loja
                    {renderSortIcon('store')}
                  </button>
                </div>
                <div className="font-medium">
                  <button 
                    onClick={() => handleSortChange('price')}
                    className={`flex items-center focus:outline-none ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-700 hover:text-black'}`}
                  >
                    Preço
                    {renderSortIcon('price')}
                  </button>
                </div>
                <div className="col-span-2 font-medium text-right">
                  <button 
                    onClick={() => handleSortChange('last_updated')}
                    className={`flex items-center justify-end focus:outline-none ml-auto ${theme === 'dark' ? 'text-muted-foreground hover:text-foreground' : 'text-gray-700 hover:text-black'}`}
                  >
                    Atualizado
                    {renderSortIcon('last_updated')}
                  </button>
                </div>
              </div>
              
              {/* Listagem de preços */}
              {sortedProducts.length > 0 ? (
                <div className={`${theme === 'dark' ? 'divide-border' : 'divide-gray-200'} divide-y`}>
                  {sortedProducts.map(product => (
                    <div 
                      key={product.id} 
                      className={`grid grid-cols-5 p-3 ${!product.available ? (theme === 'dark' ? 'bg-muted/30 text-muted-foreground' : 'bg-gray-50 text-gray-500') : (theme === 'dark' ? 'hover:bg-muted/20' : 'hover:bg-gray-50')}`}
                    >
                      <div className="col-span-2 flex items-center">
                        {product.store?.logo_url ? (
                          <img
                            src={product.store.logo_url}
                            alt={product.store.name}
                            className="h-8 w-auto mr-2"
                          />
                        ) : (
                          <div className={`h-8 w-8 rounded-full mr-2 flex items-center justify-center ${theme === 'dark' ? 'bg-muted' : 'bg-gray-200'}`}>
                            <ShoppingBag size={16} className={`${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`} />
                          </div>
                        )}
                        <span className={`font-medium ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>{product.store?.name || 'Loja'}</span>
                      </div>
                      
                      <div className={`font-bold ${product.available ? (theme === 'dark' ? 'text-green-400' : 'text-green-600') : (theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500')}`}>
                        {formatPrice(product.price)}
                      </div>
                      
                      <div className="col-span-2 flex justify-end items-center">
                        <span className={`text-sm mr-2 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>
                          {format(new Date(product.updated_at), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                        
                        <a
                          href={product.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`ml-2 p-1.5 rounded-md ${
                            product.available 
                              ? (theme === 'dark' ? 'bg-primary/80 text-primary-foreground hover:bg-primary' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200') 
                              : (theme === 'dark' ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' : 'bg-gray-100 text-gray-500 cursor-not-allowed')
                          }`}
                          title="Visitar loja"
                        >
                          <ExternalLink size={16} />
                        </a>
                        
                        <button
                          onClick={() => setSelectedStoreId(product.store?.id || null)}
                          className={`ml-1 p-1.5 rounded-md ${
                            selectedStoreId === product.store?.id
                              ? (theme === 'dark' ? 'bg-primary text-primary-foreground' : 'bg-indigo-600 text-white')
                              : (theme === 'dark' ? 'bg-muted hover:bg-muted/80 text-foreground' : 'bg-gray-100 text-gray-700 hover:bg-gray-700')
                          }`}
                          title="Ver histórico de preços"
                        >
                          <TrendingUp size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <p className={`${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>
                    Nenhum preço disponível para este jogo
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Card com o menor preço */}
          <div>
            <h2 className={`text-xl font-bold mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <DollarSign size={20} className="mr-2" />
              Melhor oferta
            </h2>
            
            {sortedProducts.length > 0 && sortedProducts[0].available ? (
              <div className={`${theme === 'dark' ? 'bg-gray-800 text-card-foreground border-green-500/70' : 'bg-white border-green-500'} rounded-lg shadow-md overflow-hidden p-4 border-2`}>
                <div className="flex items-center mb-3">
                  {sortedProducts[0].store?.logo_url ? (
                    <img
                      src={sortedProducts[0].store.logo_url}
                      alt={sortedProducts[0].store.name}
                      className="h-10 w-auto mr-2"
                    />
                  ) : (
                    <div className={`h-10 w-10 rounded-full mr-2 flex items-center justify-center ${theme === 'dark' ? 'bg-muted' : 'bg-gray-200'}`}>
                      <ShoppingBag size={20} className={`${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`} />
                    </div>
                  )}
                  <span className={`font-medium text-lg ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>{sortedProducts[0].store?.name || 'Loja'}</span>
                </div>
                
                <div className={`text-3xl font-bold mb-3 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                  {formatPrice(sortedProducts[0].price)}
                </div>
                
                <a
                  href={sortedProducts[0].product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full block py-2 px-4 rounded-md text-center font-medium transition duration-150 ${theme === 'dark' ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700'}`}
                >
                  Comprar agora
                </a>
              </div>
            ) : (
              <div className={`${theme === 'dark' ? 'bg-gray-800 text-card-foreground' : 'bg-white'} rounded-lg shadow-md overflow-hidden p-4`}>
                <p className={`${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'} text-center`}>
                  Nenhuma oferta disponível
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Seção de gráfico de histórico de preços */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <h2 className={`text-xl font-bold flex items-center mb-2 sm:mb-0 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <TrendingUp size={20} className="mr-2" />
              Histórico de preços
            </h2>
            
            <div className="flex space-x-2">
              <select
                value={historyPeriod}
                onChange={(e) => setHistoryPeriod(Number(e.target.value))}
                className={`px-3 py-1.5 border rounded-md text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground focus:ring-ring focus:border-ring' : 'border-gray-300 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500'}`}
              >
                <option value={1}>Último mês</option>
                <option value={3}>Últimos 3 meses</option>
                <option value={6}>Últimos 6 meses</option>
                <option value={12}>Último ano</option>
              </select>
              
              <button
                onClick={() => setSelectedStoreId(null)}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  selectedStoreId === null
                    ? (theme === 'dark' ? 'bg-primary text-primary-foreground' : 'bg-indigo-600 text-white')
                    : (theme === 'dark' ? 'bg-muted hover:bg-muted/80 text-foreground' : 'bg-gray-200 text-gray-800 hover:bg-gray-300')
                }`}
              >
                Todas as lojas
              </button>
            </div>
          </div>
          
          <div className={`${theme === 'dark' ? 'bg-muted text-card-foreground' : 'bg-white'} rounded-lg shadow-md overflow-hidden p-4`}>
            {historyLoading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : chartData && chartData.datasets.length > 0 ? (
              <div className="h-80">
                <Line data={chartData} options={chartOptions as any} /> 
              </div>
            ) : (
              <div className="text-center py-10">
                <Calendar className={`mx-auto h-12 w-12 mb-2 ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-400'}`} />
                <p className={`${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>
                  {selectedStoreId 
                    ? 'Histórico não disponível para esta loja'
                    : 'Nenhum histórico de preços disponível para este período'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GamePricesPage; 